import type {
  OverlayConfig as OverlayConfigV6,
  OverlayItem as OverlayItemV6,
} from "../../../api/posterTemplateService";

/**
 * 出图时服务端已烙进画面的 logo / 二维码(1006 演示机复测两轮)。
 *
 * 出图时 ai 已把 logo/二维码直接烙进产物(baseUrl 是叠过的成品;无叠加素底只在 assetMeta.baseKey,
 * 编辑器不读它 —— 画面调整的合成底图仍是成品,所以这里只能「如实说已叠、不给再叠」),回调把实际叠加位置落进
 * assetMeta.overlay → currentOverlayConfig,形状恰好是 `{version:1, logo, qr}` 三个键。
 *
 * ★判据只看这份「出图叠加记录」,**不看 task.logoUrl**。上一版(bdae05e8)写成「品牌没 logo 才算
 * 已叠」,那时详情接口 logoUrl 恒 null 碰巧成立;api 补上 logoUrl 后判据失效 → 右栏给出可拖的
 * logo 框,挪完「保存并重新合成」= 在已带 logo 的图上再叠一个(双 logo)。
 *
 * 认作「出图时已叠」的两种记录:
 *  - ai 原始记录:键只有 version/logo/qr(console 保存的形状总带 logoUploadId 等键,「加品牌」
 *    stamped 版本是 `{logo:{position}}` 无 version —— 二者都不算,继续可编辑,api 会回溯素图;
 *    stamped 版本的已叠元素由 stampedOverlay.ts 还原成可拖的现有 logo/码,底图用 asset.editBaseUrl);
 *  - console 在已叠图上存过画面调整后留下的标记 bakedLogo / bakedQr(见 withBakedMarkers)。
 */
const AI_RECORD_KEYS = new Set(["version", "logo", "qr"]);

export interface BakedOverlay {
  logo: boolean;
  qr: boolean;
}

export function bakedOverlayOf(raw: unknown): BakedOverlay {
  if (!raw || typeof raw !== "object") return { logo: false, qr: false };
  const o = raw as Partial<OverlayConfigV6>;
  if (o.version !== 1) return { logo: false, qr: false };
  const aiRecord = Object.keys(o).every((k) => AI_RECORD_KEYS.has(k));
  return {
    logo: o.bakedLogo != null || (aiRecord && o.logo != null),
    qr: o.bakedQr != null || (aiRecord && o.qr != null),
  };
}

/**
 * 已叠元素在编辑态里一律置空(不给拖框、保存时不再叠),原位置挪进 bakedLogo/bakedQr 随保存落库,
 * 下次打开仍认得出「这张图上本来就有」。
 */
export function withBakedMarkers(raw: unknown, cfg: OverlayConfigV6): OverlayConfigV6 {
  const baked = bakedOverlayOf(raw);
  if (!baked.logo && !baked.qr) return cfg;
  const o = raw as Partial<OverlayConfigV6>;
  const out: OverlayConfigV6 = { ...cfg };
  if (baked.logo) {
    out.logo = null;
    out.logoUploadId = null;
    out.bakedLogo = (o.bakedLogo ?? o.logo ?? null) as OverlayItemV6 | null;
  }
  if (baked.qr) {
    out.qr = null;
    out.qrCodeMode = null;
    out.qrCodeUploadId = null;
    out.qrCodeUrl = null;
    out.bakedQr = (o.bakedQr ?? o.qr ?? null) as OverlayItemV6 | null;
  }
  return out;
}
